import * as s from "./AlertViewStyled";
import {
  AlertActiveSummary,
  AlertList,
  AlertTraceList,
  AlertType,
  AlertViewInfo,
} from "api/interfaces/alertInterface.interface";
import { useAppSelector } from "redux/hooks";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { LEVEL_TYPE } from "api/interfaces/commonInterface.interface";
import Status, {
  StatusName,
  StatusValue,
} from "components/blocks/status/Status";
import TraceList from "./components/TraceList";
import AlertInfo from "./components/AlertInfo";
import { COLORS } from "styles/colors";

import TinyButton from "components/atoms/buttons/TinyButton";
import AlertChangeConfirm from "../../../components/templates/modal/small/alert/AlertChangeConfirm";
import AlertTypeList from "./components/AlertTypeList";
import { useLocation } from "react-router-dom";
import { FaCircleCheck } from "react-icons/fa6";
import Spinner from "components/atoms/loader/Spinner";
import NoData from "components/templates/nopage/NoData";
import { isCheckPermission } from "utils/AccountUtil";
import AlertViewHeader from "./AlertViewHeader";
import AlertWaringInfo from "./components/AlertWaringInfo";
import { formatNumber } from "utils/functions";
import { useQueryClient } from "react-query";
import AccountId from "components/atoms/text/labels/AccountId";
import { MdArrowForwardIos } from "react-icons/md";

interface Props {
  alertInfo: AlertViewInfo | null;
  currentAlert: AlertList | null;
  alertTypeList: AlertType[];
  traceList: AlertTraceList[];
  count: AlertActiveSummary;
  isModal?: boolean;
  onFinishClose: () => void;
  isFetching: boolean;
  onCloseAllStatus: () => void;
  isConfirmStatus: boolean;
  onConfirmCancel: () => void;
  onConfirmOk: () => void;
  onChangeCurrentAlert: (alertType: AlertType, value?: string) => void;
  onCheckNoConfirm: (value: boolean) => void;
  checkNoConfirm: boolean;
  historyId: string | null;
  onModal?: () => void;
  recorderId: string | null;
  isHistoryOrModal: boolean;
  alertViewRefetch: () => void;
}

const alertStatusStyled = s.AlertStatusStyled;
const AlertViewPresenter = (props: Props) => {
  const location = useLocation();
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const isMap = location.pathname.includes("map");
  const isClosed =
    (props.historyId && props.currentAlert?.status === "Closed") ||
    selectedAccount.isHidden;

  const status = {
    items: [
      <>
        <StatusName>System</StatusName>

        <StatusValue color={COLORS.RED}>
          {formatNumber(props.count.systemCount)}
        </StatusValue>
      </>,
      <>
        <StatusName>Disk</StatusName>

        <StatusValue color={COLORS.RED}>
          {formatNumber(props.count.diskCount)}
        </StatusValue>
      </>,
      <>
        <StatusName>Video</StatusName>

        <StatusValue color={COLORS.RED}>
          {formatNumber(props.count.videoCount)}
        </StatusValue>
      </>,
    ],
  };

  if (isMap) {
    if (props.isFetching) {
      return <Spinner />;
    }
    if (
      !selectedAccount.isEnabled ||
      !selectedAccount.userPermission.viewAlerts
    ) {
      return <s.AlertViewNoData></s.AlertViewNoData>;
    }
    if (!props.currentAlert && props.recorderId) {
      return (
        <s.AlertMapViewWrapper>
          <AlertWaringInfo recorderId={props.recorderId ?? ""} />
        </s.AlertMapViewWrapper>
      );
    }
    if (
      props.currentAlert?.status === "Healthy" ||
      !props.alertInfo ||
      !props.currentAlert
    ) {
      return (
        <s.AlertViewNoData>
          <FaCircleCheck color={COLORS.GREEN} size={42} />
          No Alert
        </s.AlertViewNoData>
      );
    }
    return (
      <s.AlertMapViewWrapper>
        <AlertTypeList
          alertInfo={props.alertInfo}
          alertTypeList={props.alertTypeList}
          currentAlert={props.currentAlert}
          onChangeCurrentAlert={props.onChangeCurrentAlert}
          onCheckNoConfirm={props.onCheckNoConfirm}
          checkNoConfirm={props.checkNoConfirm}
          onFinishClose={props.onFinishClose}
        />

        <AlertInfo data={props.currentAlert} />
        <TraceList
          data={props.traceList}
          alertHistoryId={props.currentAlert.alertHistoryId}
          recorderId={props.alertInfo.recorderId}
          assignee={props.currentAlert.assignee}
          isClosed={isClosed}
          alertViewRefetch={props.alertViewRefetch}
        />
      </s.AlertMapViewWrapper>
    );
  }

  return (
    <s.AlertWrapper>
      {props.alertInfo && props.currentAlert && props.isConfirmStatus && (
        <AlertChangeConfirm
          isAll
          onConfirmCancel={props.onConfirmCancel}
          onConfirmOk={props.onConfirmOk}
          alertInfo={props.alertInfo}
          currentAlert={props.currentAlert}
        />
      )}

      {props.isModal && <AlertViewHeader {...props}></AlertViewHeader>}

      <s.Inners isModal={props.isModal}>
        {props.isFetching ? (
          <Spinner />
        ) : !props.currentAlert || !props.alertInfo ? (
          <s.AlertViewNoData>
            <NoData />
          </s.AlertViewNoData>
        ) : (
          <>
            {!props.historyId && props.isModal && (
              <alertStatusStyled.AlertStatusWrapper>
                <Status status={status} />
                {isCheckPermission("manageAlertRules", selectedAccount) &&
                  !(
                    props.historyId && props.currentAlert?.status === "Closed"
                  ) && (
                    <alertStatusStyled.AlertStatusChange>
                      <TinyButton
                        onClickBtn={props.onCloseAllStatus}
                        label={
                          props.isHistoryOrModal && !props.isModal
                            ? "CLOSE"
                            : "CLOSE ALL"
                        }
                        buttonType="warning"
                        data-testid="alert-view-view-tiny-button-close-all-status"
                      />
                    </alertStatusStyled.AlertStatusChange>
                  )}
              </alertStatusStyled.AlertStatusWrapper>
            )}

            <s.InnerContents>
              {!props.isModal && <AlertViewHeader {...props}></AlertViewHeader>}
              <AlertTypeList
                alertInfo={props.alertInfo}
                alertTypeList={props.alertTypeList}
                currentAlert={props.currentAlert}
                onChangeCurrentAlert={props.onChangeCurrentAlert}
                onCheckNoConfirm={props.onCheckNoConfirm}
                checkNoConfirm={props.checkNoConfirm}
                onFinishClose={props.onFinishClose}
              />

              <AlertInfo data={props.currentAlert} />
              <TraceList
                data={props.traceList}
                alertHistoryId={props.currentAlert.alertHistoryId}
                recorderId={props.alertInfo.recorderId}
                assignee={props.currentAlert.assignee}
                isClosed={isClosed}
                alertViewRefetch={props.alertViewRefetch}
              />
            </s.InnerContents>
          </>
        )}
      </s.Inners>
    </s.AlertWrapper>
  );
};

export default AlertViewPresenter;
